// SOBRE O CLIENTE — nome e etiqueta do contato, numa peca UNICA (24/09/2026).
//
// Nasceu dentro da tela do fly (pedido dele: *"Ela pergunta antes de enviar"*) e saiu dela no
// mesmo dia, quando ele pediu o mesmo par sem precisar mandar material: o item "Etiqueta" do
// menu ⋮ da conversa. As duas telas usam ESTA peca — duas copias do bloco ficariam diferentes
// na primeira mudanca, e a esquecida e' sempre a que ninguem abre.
//
// ⛔ **Nada novo no servidor**: o nome grava pelo `PUT /contatos/:id` (o mesmo do painel do chat,
// sem trava de perfil) e a etiqueta pela peca do balcao, que usa o `POST .../etiquetas/marcar` —
// so' ACRESCENTA, entao nao apaga a etiqueta oculta do rodizio, e recusa as `nao_cliente` ([DA-722]).
// O `PUT /:id/etiquetas` devolveria 403 pra quem vende ([DA-697] p.2).
//
// ⛔ **O nome nao depende de um segundo botao** ([DA-697] p.3): grava ao sair do campo, e quem
// hospeda RECOLHE o que ficou digitado (`salvarNome` pelo ref) antes de enviar ou fechar — se a
// gravacao falhar, para e diz. `nomeGravado` `null` = ainda nao li o contato; o campo fica travado
// ate ler, senao a tela mostraria vazio pra quem tem nome e gravaria por cima.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CampoEtiquetasVenda } from '../../../components/orcamento/CampoEtiquetasVenda';
import { atendimentoApi } from '../lib/api';
import type { Contato } from '../lib/tipos';

export interface BlocoSobreClienteRef {
  /** Grava o nome se mudou. Devolve `false` so' quando TENTOU e falhou. */
  salvarNome: () => Promise<boolean>;
}

interface Props {
  contatoId: number;
  /** Trava o campo de nome (a tela do fly trava enquanto envia). */
  desabilitado?: boolean;
  /** O contato relido depois de gravar nome ou etiqueta — quem desenha o contato se atualiza. */
  onContatoAtualizado?: (c: Contato) => void;
}

export const BlocoSobreCliente = forwardRef<BlocoSobreClienteRef, Props>(function BlocoSobreCliente(
  { contatoId, desabilitado, onContatoAtualizado },
  ref,
) {
  const [nomeCliente, setNomeCliente] = useState('');
  const [nomeGravado, setNomeGravado] = useState<string | null>(null);
  const [gravandoNome, setGravandoNome] = useState(false);
  const [erroNome, setErroNome] = useState<string | null>(null);
  // O blur do campo e o botao de fechar/enviar disparam juntos (o blur vem primeiro); sem isto
  // o mesmo nome sairia em dois PUTs, porque `nomeGravado` so' muda depois da resposta.
  const emVoo = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    let vivo = true;
    setNomeGravado(null);
    atendimentoApi.obterContato(contatoId)
      .then((c) => { if (vivo) { setNomeCliente(c.nome ?? ''); setNomeGravado(c.nome ?? ''); } })
      .catch(() => { if (vivo) setErroNome('Nao consegui ler o nome deste contato.'); });
    return () => { vivo = false; };
  }, [contatoId]);

  async function gravar(novo: string): Promise<boolean> {
    setGravandoNome(true);
    setErroNome(null);
    try {
      const c = await atendimentoApi.atualizarContato(contatoId, { nome: novo });
      setNomeGravado(c.nome ?? novo);
      setNomeCliente(c.nome ?? novo);
      onContatoAtualizado?.(c);
      return true;
    } catch (e) {
      setErroNome((e as Error).message || 'Nao consegui gravar o nome.');
      return false;
    } finally {
      setGravandoNome(false);
    }
  }

  function salvarNome(): Promise<boolean> {
    if (emVoo.current) return emVoo.current;
    if (nomeGravado === null) return Promise.resolve(true);
    const novo = nomeCliente.trim();
    // Campo esvaziado nao apaga o nome gravado: o PUT exige nome, e vazio aqui e' "nao sei".
    if (!novo || novo === nomeGravado.trim()) return Promise.resolve(true);
    const p = gravar(novo).finally(() => { emVoo.current = null; });
    emVoo.current = p;
    return p;
  }

  useImperativeHandle(ref, () => ({ salvarNome }));

  // Etiqueta gravada: rele o contato (a rota devolve `etiquetas`) pra lista do chat se atualizar.
  const aoGravarEtiqueta = useCallback(() => {
    if (!onContatoAtualizado) return;
    atendimentoApi.obterContato(contatoId).then(onContatoAtualizado).catch(() => {});
  }, [contatoId, onContatoAtualizado]);

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 12, color: 'var(--text-color)', marginBottom: 6 }}>SOBRE O CLIENTE</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 40%', minWidth: 0 }}>
          <label style={{ fontSize: 11, color: 'var(--text-color)', opacity: 0.8, display: 'block', marginBottom: 2 }}>Nome</label>
          <input
            value={nomeCliente}
            disabled={desabilitado || gravandoNome || nomeGravado === null}
            onChange={(e) => { setNomeCliente(e.target.value); setErroNome(null); }}
            onBlur={() => void salvarNome()}
            onKeyDown={(e) => { if (e.key === 'Enter') void salvarNome(); }}
            placeholder={nomeGravado === null ? 'carregando...' : 'Pergunte o nome do cliente'}
            className="no-upper"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ fontSize: 11, color: 'var(--text-color)', opacity: 0.8, display: 'block', marginBottom: 2 }}>Etiqueta — a área em que ele trabalha</label>
          {/* Mesma altura do Nome (pedido dele, 24/09/2026): instrucao curta numa linha so'
              — o rotulo de cima ja diz o que e' — e o piso de altura igual ao do input.
              Com varias etiquetas o campo ainda cresce: e' a regra da casa de mostrar todas. */}
          <CampoEtiquetasVenda clienteId={0} contatoId={contatoId} semRotulo onGravou={aoGravarEtiqueta}
            instrucao="Escolha a etiqueta..." alturaMin={36} />
        </div>
      </div>
      {erroNome && (
        <div style={{ fontSize: 11, fontWeight: 700, color: '#000', background: '#fde68a', padding: '2px 8px', borderRadius: 6, marginTop: 4, display: 'inline-block' }}>
          {erroNome}
        </div>
      )}
    </div>
  );
});
